/**
 * Simply Steaks - turntable player (hero + featured products).
 *
 * Each [data-turntable] element autoplays its clip back and forth ("ping-pong"
 * so there is never a visible jump) while it is on screen. Touching the
 * element takes over: dragging sideways turns it forward or backward.
 * Vertical swipes and the mouse wheel are NOT captured, so the page always
 * scrolls normally - nothing ever pins or locks the page.
 *
 * Also: arrow keys / Home / End when focused (role="slider"), a pause button
 * (WCAG: moving content must be pausable), prefers-reduced-motion = poster only.
 *
 * Loading (unchanged idea, one implementation for every clip):
 * - Phones: pre-rendered frame sequence on a <canvas> (frame-scrub.js), which
 *   starts moving after the first ~130 KB and works on every phone.
 * - Laptops: a tiny "lite" clip first, then the sharp clip swaps in behind it
 *   at the same playhead.
 */
(function () {
  'use strict';

  if (window.__ssTurntable) {
    window.__ssTurntable.scan(document);
    return;
  }

  var STALL_MS = 8000;
  var MAX_ATTEMPTS = 4;
  var STUCK_SEEK_MS = 2500;
  var MAX_REBUILDS = 2;
  var DRAG_EASE_SECONDS = 0.04;
  var RESUME_AFTER_MS = 1800;
  var KEY_RESUME_AFTER_MS = 4000;
  var DEFAULT_PERIOD = 4;
  var FRAME_FPS = 12; // frame sequences were built at this rate

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var coarse = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  var smallQuery = window.matchMedia('(max-width: 860px)');
  function isMobile() {
    return coarse || smallQuery.matches;
  }
  function clamp(value, min, max) {
    return Math.min(max === undefined ? 1 : max, Math.max(min === undefined ? 0 : min, value));
  }

  var startedAt = Date.now();
  var heroFull = !document.querySelector('[data-turntable][data-rank="0"]');
  var userReady = false;
  var instances = [];
  var loopFrame = 0;
  var lastTime = 0;

  function announceHeroFull() {
    if (heroFull) return;
    heroFull = true;
    document.dispatchEvent(new Event('ss:hero-full'));
  }
  function mayUpgrade() {
    return heroFull || Date.now() - startedAt > 25000;
  }

  /* -------------------------------------------------------- downloads */
  // One shared queue: the hero's clip first, then the rest, two at a time.
  var queue = [];
  var activeDownloads = 0;
  function maxParallel() {
    var connection = navigator.connection || {};
    return connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '') ? 1 : 2;
  }
  function enqueue(priority, run) {
    queue.push({ priority: priority, run: run });
    queue.sort(function (a, b) {
      return a.priority - b.priority;
    });
    pumpQueue();
  }
  function pumpQueue() {
    while (activeDownloads < maxParallel() && queue.length) {
      var task = queue.shift();
      activeDownloads += 1;
      runTask(task);
    }
  }
  function runTask(task) {
    var finished = false;
    task.run(function () {
      if (finished) return;
      finished = true;
      activeDownloads -= 1;
      pumpQueue();
    });
  }

  // Downloads `source` into a Blob (aborting if no bytes arrive for STALL_MS).
  function download(source, abort, onBlob, onFailure) {
    var stallTimer = 0;
    function armStall() {
      window.clearTimeout(stallTimer);
      stallTimer = window.setTimeout(function () {
        abort.abort();
      }, STALL_MS);
    }
    armStall();
    fetch(source, { signal: abort.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Clip failed: ' + response.status);
        var type = response.headers.get('content-type') || 'video/mp4';
        if (!response.body || !response.body.getReader) return response.blob();
        var reader = response.body.getReader();
        var chunks = [];
        function pump() {
          armStall();
          return reader.read().then(function (result) {
            if (result.done) return new Blob(chunks, { type: type });
            chunks.push(result.value);
            return pump();
          });
        }
        return pump();
      })
      .then(function (blob) {
        window.clearTimeout(stallTimer);
        if (abort.signal.aborted) return;
        onBlob(blob);
      })
      .catch(function () {
        window.clearTimeout(stallTimer);
        onFailure();
      });
  }

  function primeVideo(video) {
    if (!isMobile() || !video) return;
    var attempt = video.play();
    if (attempt && attempt.then) {
      attempt
        .then(function () {
          video.pause();
        })
        .catch(function () {
          /* poster stays; a later gesture retries */
        });
    }
  }

  function parsePosition(text) {
    var parts = String(text || '50% 50%').split(/\s+/);
    var x = parseFloat(parts[0]) / 100;
    var y = parseFloat(parts[1]) / 100;
    return [isFinite(x) ? x : 0.5, isFinite(y) ? y : 0.5];
  }

  /* --------------------------------------------------------- instance */
  function create(root) {
    var reduceMotion = reduceMotionQuery.matches;
    var mobile = isMobile();
    var rank = parseInt(root.getAttribute('data-rank'), 10) || 0;
    var framesMeta = root.getAttribute('data-frames-meta');
    var useFrames = !!(framesMeta && window.__ssFrames && mobile && !reduceMotion);
    var liteUrl = root.getAttribute('data-lite-clip') || '';
    var fullUrl = (mobile && root.getAttribute('data-mobile-clip')) || root.getAttribute('data-clip') || '';
    var position = parsePosition(
      (mobile && root.getAttribute('data-position-mobile')) || root.getAttribute('data-position')
    );
    var toggle = root.parentElement && root.parentElement.querySelector('[data-turntable-toggle]');
    // Inside the featured slider only the current product autoplays, and the
    // slider (not the clipped panel) decides when it is near / in view.
    var panel = root.closest('[data-turntable-panel]');
    var scope = root.closest('[data-turntable-scope]') || root;
    function isCurrent() {
      return !panel || !scope.hasAttribute('data-fslider-ready') || panel.hasAttribute('data-current');
    }
    var hint = root.querySelector('[data-turntable-hint]');

    var s = {
      video: null,
      objectUrl: '',
      source: '',
      tier: '', // 'lite' | 'full'
      ready: false,
      loading: false,
      failed: false,
      attempts: 0,
      retryAt: 0,
      liteFailed: false,
      upgrading: false,
      upgradeAbort: null,
      abort: null,
      rebuilds: 0,
      seekingSince: 0
    };
    var frames = null;
    var near = false;
    var inView = false;
    var destroyed = false;
    var queued = false;
    var upgradeQueued = false;

    // Playback state. p is the target position 0..1; shown eases toward it
    // while the user is dragging; autoplay moves both together.
    var p = 0;
    var shown = 0;
    var dir = 1;
    var mode = 'auto'; // 'auto' | 'drag' | 'hold'
    var resumeAt = 0;
    var paused = false;
    var period = DEFAULT_PERIOD;
    var dragId = null;
    var dragStartX = 0;
    var dragStartP = 0;
    var lastX = 0;
    var painted = false;

    // The hero (rank 0) upgrades straight away - it is what everything else
    // waits for. Other clips wait until the hero's sharp clip is in.
    function maybeUpgrade() {
      if (inView && isCurrent() && s.tier === 'lite' && (rank === 0 || mayUpgrade())) requestUpgrade();
    }

    function useLite() {
      return !!liteUrl && !mobile && !s.liteFailed && typeof fetch === 'function';
    }

    function markPainted() {
      if (painted) return;
      painted = true;
      root.setAttribute('data-painted', '');
    }

    function setState(value) {
      root.setAttribute('data-video-state', value);
    }
    setState('idle');

    /* ----------------------------------------------------- frames path */
    function startFrames() {
      var canvas = document.createElement('canvas');
      canvas.className = 'turntable__media turntable__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      setState('loading');
      frames = window.__ssFrames.create({
        metaUrl: framesMeta,
        canvas: canvas,
        host: root,
        mount: function (el) {
          root.insertBefore(el, root.firstChild);
        },
        position: position,
        rank: rank,
        // The hero reveals progressively (it's the first thing on the page);
        // the featured products wait for a couple of sheets so dragging them
        // never jumps across an unloaded stretch of the clip.
        smooth: rank > 0,
        onFirstFrame: function () {
          markPainted();
          setState('ready');
          s.ready = true;
        },
        onComplete: function () {
          if (rank === 0) announceHeroFull();
        },
        onFail: function () {
          // Frames unavailable: fall back to the (slower) video path.
          frames = null;
          useFrames = false;
          s.ready = false;
          queued = false;
          if (near) requestLoad();
        }
      });
      if (frames.setActive) frames.setActive(rank === 0);
      frames.start();
    }

    /* ------------------------------------------------------ video path */
    function buildVideo(url) {
      var video = document.createElement('video');
      video.className = 'turntable__media turntable__video';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.src = url;
      return video;
    }

    function attach(url, source, done) {
      var video = buildVideo(url);
      video.addEventListener(
        'loadedmetadata',
        function () {
          if (s.video !== video) return;
          s.ready = true;
          s.loading = false;
          s.tier = liteUrl && source === liteUrl ? 'lite' : 'full';
          period = video.duration || DEFAULT_PERIOD;
          setState('ready');
          if (s.tier === 'full' && rank === 0) announceHeroFull();
          if (done) done();
          if (s.tier === 'lite') maybeUpgrade();
        },
        { once: true }
      );
      video.addEventListener(
        'loadeddata',
        function () {
          if (userReady && s.video === video) primeVideo(video);
        },
        { once: true }
      );
      video.addEventListener(
        'error',
        function () {
          if (s.video !== video) return;
          video.remove();
          s.video = null;
          s.ready = false;
          s.loading = false;
          s.failed = true;
          root.removeAttribute('data-painted');
          painted = false;
          setState('failed');
          if (rank === 0) announceHeroFull();
          if (done) done();
        },
        { once: true }
      );
      video.addEventListener('seeked', function () {
        if (s.video !== video) return;
        s.seekingSince = 0;
        // Only retire the poster once a real frame is on screen.
        if (video.readyState >= 2) markPainted();
      });
      root.insertBefore(video, root.firstChild);
      s.video = video;
      s.source = source;
    }

    function requestLoad() {
      if (queued || destroyed || reduceMotion || s.failed || s.ready) return;
      queued = true;
      if (useFrames) {
        startFrames();
        return;
      }
      enqueue(rank, function (done) {
        queued = false;
        loadFirst(done);
      });
    }

    function loadFirst(done) {
      var lite = useLite();
      var source = lite ? liteUrl : fullUrl;
      if (destroyed || !source || s.ready || s.failed || Date.now() < s.retryAt) {
        done();
        return;
      }
      s.loading = true;
      s.source = source;
      setState('loading');

      if (typeof fetch !== 'function') {
        attach(source, source, done);
        return;
      }
      var abort = new AbortController();
      s.abort = abort;
      download(
        source,
        abort,
        function (blob) {
          if (destroyed || s.source !== source) return done();
          s.objectUrl = URL.createObjectURL(blob);
          attach(s.objectUrl, source, done);
        },
        function () {
          if (destroyed || s.source !== source) return done();
          s.source = '';
          s.loading = false;
          if (lite) {
            // The lite clip is only an accelerator: go straight to the full one.
            s.liteFailed = true;
          } else {
            s.attempts += 1;
            s.retryAt = Date.now() + 1500 * s.attempts;
            if (s.attempts >= MAX_ATTEMPTS) {
              // Last resort: hand the URL to <video> and let it stream.
              s.loading = true;
              s.source = fullUrl;
              attach(fullUrl, fullUrl, function () {});
              return done();
            }
          }
          done();
          queued = false;
          if (!s.ready && !s.failed) window.setTimeout(requestLoad, lite ? 0 : 1500 * s.attempts);
        }
      );
    }

    // Swap the lite clip for the sharp one behind it, keeping the playhead.
    function requestUpgrade() {
      if (upgradeQueued || s.upgrading || destroyed || s.tier !== 'lite' || !fullUrl || fullUrl === liteUrl) return;
      upgradeQueued = true;
      enqueue(100 + rank, function (done) {
        upgradeQueued = false;
        if (destroyed || s.tier !== 'lite') return done();
        s.upgrading = true;
        var abort = new AbortController();
        s.upgradeAbort = abort;
        download(
          fullUrl,
          abort,
          function (blob) {
            var url = URL.createObjectURL(blob);
            var next = buildVideo(url);
            var settled = false;
            function finish() {
              if (settled) return;
              settled = true;
              done();
            }
            function swap() {
              if (settled) return;
              if (destroyed || s.upgradeAbort !== abort) {
                next.remove();
                URL.revokeObjectURL(url);
                return finish();
              }
              var old = s.video;
              var oldUrl = s.objectUrl;
              s.video = next;
              s.objectUrl = url;
              s.source = fullUrl;
              s.tier = 'full';
              s.upgrading = false;
              s.seekingSince = 0;
              period = next.duration || period;
              if (old) old.remove();
              if (oldUrl) URL.revokeObjectURL(oldUrl);
              if (next.readyState >= 2) markPainted();
              if (rank === 0) announceHeroFull();
              finish();
            }
            next.addEventListener('seeked', function () {
              if (s.video === next) {
                s.seekingSince = 0;
                if (next.readyState >= 2) markPainted();
              }
            });
            next.addEventListener(
              'loadedmetadata',
              function () {
                try {
                  next.currentTime = clamp(shown, 0, 0.999) * (next.duration || 1);
                } catch (error) {
                  swap();
                }
              },
              { once: true }
            );
            next.addEventListener('seeked', swap, { once: true });
            next.addEventListener(
              'error',
              function () {
                next.remove();
                URL.revokeObjectURL(url);
                s.upgrading = false;
                s.tier = 'full'; // stop trying; the lite clip keeps working
                if (rank === 0) announceHeroFull();
                finish();
              },
              { once: true }
            );
            root.insertBefore(next, root.firstChild);
          },
          function () {
            s.upgrading = false;
            s.tier = 'full';
            if (rank === 0) announceHeroFull();
            done();
          }
        );
      });
    }

    // Some mobile browsers leave `seeking` permanently true; a fresh element
    // built from the blob we already hold recovers.
    function rebuild() {
      if (!s.video || !s.objectUrl || s.rebuilds >= MAX_REBUILDS) return;
      s.rebuilds += 1;
      var source = s.source;
      s.video.remove();
      s.video = null;
      s.ready = false;
      s.seekingSince = 0;
      root.removeAttribute('data-painted');
      painted = false;
      attach(s.objectUrl, source, null);
    }

    /* -------------------------------------------------------- rendering */
    function render(dt) {
      if (frames) {
        frames.setActive(rank === 0 || near);
        frames.update(shown, dt);
        return;
      }
      var video = s.video;
      if (!video || !s.ready) return;
      if (video.seeking) {
        if (!s.seekingSince) s.seekingSince = Date.now();
        if (Date.now() - s.seekingSince > STUCK_SEEK_MS) rebuild();
        return;
      }
      s.seekingSince = 0;
      var duration = video.duration || 1;
      var wanted = clamp(shown, 0, 0.999) * duration;
      var epsilon = mobile ? 0.02 : 0.012;
      if (Math.abs(video.currentTime - wanted) > epsilon) {
        try {
          video.currentTime = wanted;
        } catch (error) {
          /* keep the last painted frame */
        }
      }
    }

    function step(dt, now) {
      if (destroyed || reduceMotion) return;
      if (frames && !s.ready && !painted) {
        render(dt);
        return;
      }
      if (mode === 'hold' && now >= resumeAt && !paused) mode = 'auto';
      if (mode === 'auto' && !paused) {
        var span = frames ? (frames.frameCount() || 1) / FRAME_FPS : period;
        p += (dir * dt) / Math.max(span, 0.5);
        if (p >= 1) {
          p = 1;
          dir = -1;
        } else if (p <= 0) {
          p = 0;
          dir = 1;
        }
        shown = p;
      } else {
        shown += (p - shown) * (1 - Math.exp(-dt / DRAG_EASE_SECONDS));
        if (Math.abs(p - shown) < 0.0005) shown = p;
      }
      render(dt);
    }

    /* ----------------------------------------------------------- input */
    function interacted() {
      root.classList.add('is-touched');
      if (hint) hint.hidden = true;
    }

    function onPointerDown(event) {
      if (reduceMotion) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest && event.target.closest('button, a')) return;
      dragId = event.pointerId;
      dragStartX = lastX = event.clientX;
      dragStartP = p;
      mode = 'drag';
      root.classList.add('is-dragging');
      try {
        root.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is optional */
      }
      interacted();
    }
    function onPointerMove(event) {
      if (dragId !== event.pointerId || mode !== 'drag') return;
      var width = Math.max(root.clientWidth, 200);
      p = clamp(dragStartP + (event.clientX - dragStartX) / (width * 0.9));
      if (event.clientX !== lastX) dir = event.clientX > lastX ? 1 : -1;
      lastX = event.clientX;
      if (p <= 0) dir = 1;
      if (p >= 1) dir = -1;
    }
    function endDrag(event) {
      if (dragId === null || (event && dragId !== event.pointerId)) return;
      dragId = null;
      root.classList.remove('is-dragging');
      try {
        if (event) root.releasePointerCapture(event.pointerId);
      } catch (error) {
        /* already released */
      }
      mode = 'hold';
      resumeAt = performance.now() + RESUME_AFTER_MS;
    }
    function onWheel(event) {
      // Only a sideways wheel/trackpad swipe turns it; vertical wheel scrolls the page.
      if (reduceMotion || Math.abs(event.deltaX) <= Math.abs(event.deltaY) * 1.2 || !event.deltaX) return;
      event.preventDefault();
      p = clamp(p + event.deltaX / (Math.max(root.clientWidth, 200) * 1.4));
      dir = event.deltaX > 0 ? 1 : -1;
      mode = 'hold';
      resumeAt = performance.now() + RESUME_AFTER_MS;
      interacted();
    }
    function onKey(event) {
      if (reduceMotion) return;
      var handled = true;
      if (event.key === 'ArrowRight') {
        p = clamp(p + 0.05);
        dir = 1;
      } else if (event.key === 'ArrowLeft') {
        p = clamp(p - 0.05);
        dir = -1;
      } else if (event.key === 'Home') {
        p = 0;
        dir = 1;
      } else if (event.key === 'End') {
        p = 1;
        dir = -1;
      } else {
        handled = false;
      }
      if (!handled) return;
      event.preventDefault();
      mode = 'hold';
      resumeAt = performance.now() + KEY_RESUME_AFTER_MS;
      root.setAttribute('aria-valuenow', String(Math.round(p * 100)));
      interacted();
    }
    function onToggle(event) {
      event.stopPropagation();
      paused = !paused;
      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggle.setAttribute('aria-label', paused ? 'Play rotation' : 'Pause rotation');
      toggle.classList.toggle('is-paused', paused);
      if (!paused) {
        mode = 'auto';
      }
    }

    if (!reduceMotion) {
      root.addEventListener('pointerdown', onPointerDown);
      root.addEventListener('pointermove', onPointerMove);
      root.addEventListener('pointerup', endDrag);
      root.addEventListener('pointercancel', endDrag);
      root.addEventListener('lostpointercapture', endDrag);
      root.addEventListener('wheel', onWheel, { passive: false });
      root.addEventListener('keydown', onKey);
      root.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });
      if (toggle) toggle.addEventListener('click', onToggle);
    } else {
      if (toggle) toggle.hidden = true;
      if (hint) hint.hidden = true;
      root.removeAttribute('tabindex');
      root.removeAttribute('role');
    }

    /* ------------------------------------------------------ visibility */
    var nearObserver = null;
    var viewObserver = null;
    if (typeof IntersectionObserver !== 'undefined') {
      nearObserver = new IntersectionObserver(
        function (entries) {
          near = entries[entries.length - 1].isIntersecting;
          if (near) requestLoad();
        },
        { rootMargin: '900px 0px 900px 0px' }
      );
      nearObserver.observe(scope);
      viewObserver = new IntersectionObserver(
        function (entries) {
          inView = entries[entries.length - 1].isIntersecting;
          if (inView) {
            wake();
            maybeUpgrade();
          }
        },
        { threshold: 0 }
      );
      viewObserver.observe(scope);
    } else {
      near = inView = true;
      requestLoad();
    }
    document.addEventListener('ss:hero-full', maybeUpgrade);
    document.addEventListener('ss:panel-change', function () {
      if (!isCurrent()) return;
      wake();
      maybeUpgrade();
    });
    window.setTimeout(function () {
      if (inView && isCurrent() && s.tier === 'lite') requestUpgrade();
    }, 25500);

    if (rank === 0 && reduceMotion) announceHeroFull();

    return {
      root: root,
      isActive: function () {
        return inView && isCurrent() && !destroyed;
      },
      step: step,
      destroy: function () {
        destroyed = true;
        if (nearObserver) nearObserver.disconnect();
        if (viewObserver) viewObserver.disconnect();
        if (s.abort) s.abort.abort();
        if (s.upgradeAbort) s.upgradeAbort.abort();
        if (frames) frames.destroy();
        if (s.video) s.video.remove();
        if (s.objectUrl) URL.revokeObjectURL(s.objectUrl);
      },
      prime: function () {
        if (s.video) primeVideo(s.video);
      }
    };
  }

  /* ------------------------------------------------------------- loop */
  function tick(now) {
    loopFrame = 0;
    var dt = lastTime ? Math.min((now - lastTime) / 1000, 0.1) : 0.016;
    lastTime = now;
    var any = false;
    for (var i = 0; i < instances.length; i += 1) {
      if (instances[i].isActive()) {
        any = true;
        instances[i].step(dt, now);
      }
    }
    if (any) {
      loopFrame = window.requestAnimationFrame(tick);
    } else {
      lastTime = 0;
    }
  }
  function wake() {
    if (!loopFrame) loopFrame = window.requestAnimationFrame(tick);
  }

  function onFirstGesture() {
    userReady = true;
    instances.forEach(function (instance) {
      instance.prime();
    });
  }
  window.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
  window.addEventListener('touchstart', onFirstGesture, { once: true, passive: true });

  var registry = new WeakMap();
  function scan(context) {
    var roots = [];
    if (context.matches && context.matches('[data-turntable]')) roots.push(context);
    roots = roots.concat(Array.prototype.slice.call(context.querySelectorAll('[data-turntable]')));
    roots.forEach(function (root) {
      if (registry.has(root)) return;
      var instance = create(root);
      registry.set(root, instance);
      instances.push(instance);
    });
    wake();
  }

  window.__ssTurntable = { scan: scan };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll('[data-turntable]'));
    roots.forEach(function (root) {
      var instance = registry.get(root);
      if (!instance) return;
      instance.destroy();
      registry.delete(root);
      instances.splice(instances.indexOf(instance), 1);
    });
  });
})();
